import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

import { ELEMENTS_RECIPES } from '@constants/ElementsLimit';
import { setQueryParams } from '@utils/setQueryParams';
import { getURLProps } from '@utils/setPagesUrl';
import type { GetRecipesWithFiltersQuery } from '@schema/cart';

import API from '@api';
import { withSsrCache } from '../../ssrCache';

const RECIPES_STALE_TIME = 5 * 60 * 1000;
const RECIPES_GC_TIME = 10 * 60 * 1000;

type GetRecipesDataT = NonNullable<
  GetRecipesWithFiltersQuery['getRecipesWithFilters']
>;

export type RecipeItemT = NonNullable<GetRecipesDataT['recipeItems']>[number];

/** Данные фильтров, приходящие вместе со списком рецептов */
export type RecipesSideDataT = {
  promoBludoItems: NonNullable<GetRecipesDataT['promoBludoItems']>;
  bludoItems: NonNullable<GetRecipesDataT['bludoItems']>;
  naznachenieItems: NonNullable<GetRecipesDataT['naznachenieItems']>;
  obrabotkaItems: NonNullable<GetRecipesDataT['obrabotkaItems']>;
  slozhnostItems: NonNullable<GetRecipesDataT['slozhnostItems']>;
  typesItems: NonNullable<GetRecipesDataT['typesItems']>;
  promoBludoProducts: NonNullable<GetRecipesDataT['promoBludoProducts']>;
};

export const recipesInfiniteQueryOptions = (
  params: Omit<Parameters<typeof API.getRecipesWithFilters>[0], 'page'>,
  initialPage = 1
) => {
  const cacheKey = ['recipes', 'infinite', params];

  return withSsrCache(
    infiniteQueryOptions({
      queryKey: cacheKey,
      queryFn: ({ pageParam }) => {
        const perPage = params.setLimit ?? ELEMENTS_RECIPES;

        return API.getRecipesWithFilters({ ...params, page: pageParam }).then(
          res => {
            const data = res.getRecipesWithFilters;
            const totalCountPages = Math.ceil(data.totalCount / perPage);

            const sideData: RecipesSideDataT = {
              promoBludoItems: data.promoBludoItems || [],
              bludoItems: data.bludoItems || [],
              naznachenieItems: data.naznachenieItems || [],
              obrabotkaItems: data.obrabotkaItems || [],
              slozhnostItems: data.slozhnostItems || [],
              typesItems: data.typesItems || [],
              promoBludoProducts: data.promoBludoProducts || []
            };

            return {
              items: data.recipeItems || [],
              total: data.totalCount,
              pagination: {
                total: totalCountPages,
                current: pageParam
              },
              sideData
            };
          }
        );
      },
      initialPageParam: initialPage,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const totalCount = lastPage.total;
        const limit = params.setLimit ?? ELEMENTS_RECIPES;

        return lastPageParam * limit < totalCount
          ? lastPageParam + 1
          : undefined;
      },
      placeholderData: keepPreviousData,
      staleTime: RECIPES_STALE_TIME,
      gcTime: RECIPES_GC_TIME
    })
  );
};

export type RecipesInfiniteQueryOptionsT = ReturnType<
  typeof recipesInfiniteQueryOptions
>;

/** Популярные рецепты (блок «Интересное» в AskChef, блог шефа и т.п.) */
export const recipesPopularQueryOptions = (
  params: Parameters<typeof API.getRecipesPopular>[0]
) =>
  withSsrCache(
    queryOptions({
      queryKey: ['recipesPopular', params],
      queryFn: () => API.getRecipesPopular(params),
      select: res => res.getRecipesPopular ?? [],
      staleTime: RECIPES_STALE_TIME,
      gcTime: RECIPES_GC_TIME
    })
  );

export const useRecipesPopularQuery = (
  params: Parameters<typeof API.getRecipesPopular>[0]
) => useQuery(recipesPopularQueryOptions(params));

/**
 * Блок «Это интересно» на /faq — популярные рецепты с СОБСТВЕННЫМ лимитом,
 * то есть и с собственным ключом (['recipesPopular', {setLimit: 2}]).
 * Отдельная фабрика, чтобы префетч и компонент не разъехались по параметрам:
 * с инлайновым {setLimit: 2} в компоненте SSR-ключ пришлось бы повторять
 * руками, а любая правка лимита молча ломала бы попадание в кэш.
 */
export const ASK_CHEF_INTERESTING_LIMIT = 2;

export const askChefInterestingRecipesQueryOptions = () =>
  recipesPopularQueryOptions({ setLimit: ASK_CHEF_INTERESTING_LIMIT });

/** Справочники фильтров рецептов (форма добавления рецепта) */
export const recipesFiltersQueryOptions = () =>
  withSsrCache(
    queryOptions({
      queryKey: ['recipesFilters'],
      queryFn: () => API.getRecipesFilters(),
      select: res => res.getRecipesFilters,
      staleTime: RECIPES_STALE_TIME,
      gcTime: RECIPES_GC_TIME
    })
  );

export const RECIPE_BY_ID_QUERY_KEY = 'recipeById';

export const recipeByIdQueryOptions = (id: number) =>
  withSsrCache(
    queryOptions({
      queryKey: [RECIPE_BY_ID_QUERY_KEY, id],
      queryFn: () => API.getRecipeById({ id }),
      select: res => res.getRecipeById,
      staleTime: RECIPES_STALE_TIME,
      gcTime: RECIPES_GC_TIME
    }),
    'product'
  );

/** Добавление/удаление рецепта из избранного (страница рецепта) */
export const useToggleRecipeFavoriteMutation = () =>
  useMutation({
    mutationFn: async ({
      recipeId,
      isFavorite
    }: {
      recipeId: number;
      /** Текущее состояние: true — рецепт уже в избранном, удаляем */
      isFavorite: boolean;
    }) => {
      if (isFavorite) {
        await API.removeRecipeFromFavoritesList({
          data: { objectId: recipeId }
        });
      } else {
        await API.addRecipeToFavoritesList({ data: { objectId: recipeId } });
      }
    }
  });

/**
 * Оценка пользователя после голосования (модалка «Оцените рецепт») пишется
 * прямо в кэш рецепта, без рефетча.
 */
export const setRecipeVotedRatingInCache = (
  queryClient: QueryClient,
  id: number,
  rating: number
): void => {
  queryClient.setQueryData(recipeByIdQueryOptions(id).queryKey, prev =>
    prev
      ? {
          ...prev,
          getRecipeById: { ...prev.getRecipeById, _votedRating: rating }
        }
      : prev
  );
};

/**
 * Наборы «Готовьте как Шеф» на странице товара. Фильтрация рецептов,
 * у которых все ингредиенты в наличии, перенесена из компонента в select.
 */
export const promoRecipesByProductIdQueryOptions = (productId: number) =>
  withSsrCache(
    queryOptions({
      queryKey: ['promoRecipesByProductId', productId],
      queryFn: () => API.getPromoRecipesByProductId({ productId }),
      select: res =>
        res.getPromoRecipesByProductId?.filter(item =>
          item?.recipeIngredients?.every(
            i => !!Number(i?.ingredientsItems?.[0]?.catalogItem?.gds_count)
          )
        ) ?? [],
      staleTime: RECIPES_STALE_TIME,
      gcTime: RECIPES_GC_TIME
    }),
    'product'
  );

export const usePromoRecipesByProductIdQuery = (productId: number) =>
  useQuery(promoRecipesByProductIdQueryOptions(productId));

/**
 * ЭТАП 2 — рекомендованный рецепт под каталогом (/catalog/:id, блок
 * «рецепт + SEO-текст» под «Популярными товарами»). Раньше грузился
 * императивно в Catalog/component.getMainInfo (setState в эффекте) — SSR
 * отдавал скелетон. xStoreId в ключе (в сам запрос не передаётся): смена
 * магазина перезапрашивает — замена старой mobx-reaction.
 */
export const recipesRecommendQueryOptions = (params: {
  xStoreId?: number | string | null;
}) =>
  withSsrCache(
    queryOptions({
      queryKey: ['recipesRecommend', params],
      queryFn: () => API.getRecipesRecommend(),
      select: res => res.getRecipesRecommend?.[0] ?? null,
      staleTime: RECIPES_STALE_TIME,
      gcTime: RECIPES_GC_TIME,
      placeholderData: keepPreviousData
    })
  );

/**
 * ЭТАП 2 — рецепты с этим товаром (страница товара, блок «рецепт товара»
 * и вкладка «Рецепты»). Префетчится для SSR /product/:id (ssrPrefetch).
 */
export const productRecipesQueryOptions = (id: number) =>
  withSsrCache(
    queryOptions({
      queryKey: ['productRecipes', id],
      queryFn: () => API.getProductRecipes({ id }),
      select: res => res.getProductRecipesById?.recipeItems ?? [],
      staleTime: RECIPES_STALE_TIME,
      gcTime: RECIPES_GC_TIME
    }),
    'product'
  );

/** Фильтры списка рецептов, живущие в /r/-хвосте (порядок как в компоненте) */
const RECIPES_FILTER_PARAMS = [
  'bludo',
  'obrabotka',
  'search',
  'slozhnost',
  'type',
  'naznachenie',
  'productPromo',
  'sauce'
];

/**
 * ЭТАП 2 — параметры списка рецептов из URL: фильтры /r/bludo/N,
 * /r/obrabotka/N, /r/slozhnost/N, /r/type/N, /r/naznachenie/N,
 * /r/search/…, /r/productPromo/1, /r/sauce/N + страница /r/page/N.
 * ЕДИНЫЙ источник для компонента (routes/Recipes) и SSR-префетча: ключ
 * обязан совпасть байт-в-байт (setQueryParams кладёт только НЕ-дефолтные
 * значения и приводит всё, кроме search, к числу).
 */
export const buildRecipesListParams = (
  pathname: string
): {
  params: Omit<Parameters<typeof API.getRecipesWithFilters>[0], 'page'>;
  page: number;
} => {
  const paramsObj = setQueryParams(RECIPES_FILTER_PARAMS, { pathname });
  const { page } = getURLProps({ pathname });

  return {
    params: {
      ...paramsObj,
      productsPromoIds:
        paramsObj?.sauce && Number(paramsObj?.sauce) !== 0
          ? [Number(paramsObj.sauce)]
          : [],
      isProductPromo: !!paramsObj?.productPromo,
      setLimit: ELEMENTS_RECIPES
    },
    page: parseInt(`${page}`, 10) || 1
  };
};

/**
 * ЭТАП 2 — id рецепта из сегмента URL: «krevetki-…-123» → 123, «123» → 123.
 * Извлечено 1:1 из useRecipeParsedId (routes/Recipe/component) — общий
 * источник для компонента, префетча и loader'а роута. NaN — битый адрес.
 */
export const parseRecipeUrlId = (value: string): number => {
  const tail = value.includes('-') ? value.split('-').pop() ?? '' : value;

  return Number(tail);
};
